import React, { useState } from "react";
import {
  Lobby,
  LobbySettings as LobbySettingsType,
} from "../../../../services/api/LobbyApi";
import { api } from "../../../../services/api";
import styles from "./LobbySettings.module.scss";

interface LobbySettingsProps {
  lobby: Lobby;
  onClose: () => void;
}

const LobbySettings: React.FC<LobbySettingsProps> = ({ lobby, onClose }) => {
  const [title, setTitle] = useState(lobby.title);
  const [description, setDescription] = useState(lobby.description || "");
  const [maxPlayers, setMaxPlayers] = useState(lobby.maxPlayers);
  const [gameMode, setGameMode] = useState(lobby.preferences.gameMode || "");
  const [region, setRegion] = useState(lobby.preferences.region || "");
  const [skillLevel, setSkillLevel] = useState<"casual" | "competitive" | "">(
    lobby.preferences.skillLevel || ""
  );
  const [voiceRequired, setVoiceRequired] = useState(
    lobby.preferences.voiceRequired || false
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim() || title.trim().length > 100) {
      setError("Title must be 1-100 characters");
      return;
    }
    if (maxPlayers < 2 || maxPlayers > 20 || !Number.isInteger(maxPlayers)) {
      setError("Max players must be an integer between 2 and 20");
      return;
    }
    if (maxPlayers < lobby.members.length) {
      setError(
        `Max players cannot be less than current member count (${lobby.members.length})`
      );
      return;
    }

    const settings: LobbySettingsType = {};
    if (title.trim() !== lobby.title) settings.title = title.trim();
    if (description !== (lobby.description || ""))
      settings.description = description;
    if (maxPlayers !== lobby.maxPlayers) settings.maxPlayers = maxPlayers;
    if (gameMode !== (lobby.preferences.gameMode || ""))
      settings.gameMode = gameMode || undefined;
    if (region !== (lobby.preferences.region || ""))
      settings.region = region || undefined;
    if (skillLevel !== (lobby.preferences.skillLevel || ""))
      settings.skillLevel = skillLevel || undefined;
    if (voiceRequired !== (lobby.preferences.voiceRequired || false))
      settings.voiceRequired = voiceRequired;

    if (Object.keys(settings).length === 0) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await api.lobby.editSettings(lobby.lobbyId, settings);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>Edit Settings</h3>

        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.field}>
            <span>Title</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
            />
          </label>

          <label className={styles.field}>
            <span>Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </label>

          <label className={styles.field}>
            <span>Max Players</span>
            <input
              type="number"
              value={maxPlayers}
              onChange={(e) => setMaxPlayers(Number(e.target.value))}
              min={2}
              max={20}
            />
          </label>

          <label className={styles.field}>
            <span>Game Mode</span>
            <input
              type="text"
              value={gameMode}
              onChange={(e) => setGameMode(e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Region</span>
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>Skill Level</span>
            <select
              value={skillLevel}
              onChange={(e) => setSkillLevel(e.target.value as any)}
            >
              <option value="">Any</option>
              <option value="casual">Casual</option>
              <option value="competitive">Competitive</option>
            </select>
          </label>

          <label className={styles.checkboxField}>
            <input
              type="checkbox"
              checked={voiceRequired}
              onChange={(e) => setVoiceRequired(e.target.checked)}
            />
            <span>Voice Required</span>
          </label>

          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.saveBtn}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LobbySettings;
